import { PauseCircle, PlayCircle, RefreshCw, X } from "lucide-react";
import { FilterPopover, type FilterState } from "../../components/ui/filter-popover";
import { DateRangePicker, type DateRangeValue } from "../../components/ui/date-range-picker";
import type { ClassificationFilter, StatusFilter } from "./types";

interface CallbacksToolbarProps {
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  selectedCount: number;
  onBulkPause: () => void;
  onBulkResume: () => void;
  onClearFilters: () => void;
  onRefresh: () => void;
  isFiltered: boolean;
}

export function CallbacksToolbar(p: CallbacksToolbarProps) {
  return (
    <div className="rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-3 sm:p-3.5">
      {/* Row 1 — Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker value={p.dateRange} onChange={p.onDateRangeChange} />
        
        <FilterPopover 
          variant="callbacks"
          value={p.filters}
          onChange={p.onFilterChange}
        />

        <button
          onClick={p.onRefresh}
          aria-label="Refresh callbacks"
          className="rounded-lg p-2 text-ink-2 transition-colors hover:bg-soft hover:text-ink"
        >
          <RefreshCw className="h-4 w-4" />
        </button>

        <div className="flex-1" />

        {p.selectedCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-ink-3">{p.selectedCount} selected</span>
            <button
              onClick={p.onBulkPause}
              className="flex items-center gap-1.5 rounded-lg border border-warning/20 bg-warning-soft px-3 py-2 text-xs font-semibold text-warning transition-colors hover:bg-warning hover:text-white"
            >
              <PauseCircle className="h-4 w-4" /> Pause Selected
            </button>
            <button
              onClick={p.onBulkResume}
              className="flex items-center gap-1.5 rounded-lg border border-success/20 bg-success-soft px-3 py-2 text-xs font-semibold text-success transition-colors hover:bg-success hover:text-white"
            >
              <PlayCircle className="h-4 w-4" /> Resume Selected
            </button>
          </div>
        )}
      </div>

      {/* Row 2 — Active filter pills */}
      {p.isFiltered && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {p.filters.search && (
            <FilterPill label={`Search: ${p.filters.search}`} onClear={() => p.onFilterChange({ ...p.filters, search: undefined })} />
          )}
          {p.filters.classification && p.filters.classification !== "all" && (
            <FilterPill label={`Classification: ${p.filters.classification}`} onClear={() => p.onFilterChange({ ...p.filters, classification: undefined })} />
          )}
          {p.filters.status && p.filters.status !== "all" && (
            <FilterPill label={`Status: ${p.filters.status}`} onClear={() => p.onFilterChange({ ...p.filters, status: undefined })} />
          )}
          <button onClick={p.onClearFilters} className="text-xs font-semibold text-brand hover:underline">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

function FilterPill({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-soft px-3 py-1 text-xs font-medium text-ink-2">
      {label}
      <button onClick={onClear} aria-label={`Clear ${label}`} className="text-ink-3 hover:text-ink">
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
